import React, { useState } from 'react';
import { Download, FileSpreadsheet, FileText, BarChart3, PieChart, Loader, CheckCircle, AlertCircle, Settings } from 'lucide-react';
import { useStore } from '../store';
import { exportToAdvancedExcel, ExportOptions } from '../utils/advancedExport';

export function Export() {
  const { analysisResults, axes, workshopColumn } = useStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState<string>('');
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeCharts: true,
    includePercentages: true,
    includeDetailedAnalysis: true,
    chartTypes: ['column', 'pie'],
    colorScheme: 'professional'
  });

  const handleAdvancedExport = async () => {
    if (!analysisResults) {
      setError('لا توجد نتائج تحليل متاحة للتصدير');
      return;
    }

    if (!selectedWorkshop) {
      setError('يرجى اختيار ورشة العمل للتصدير');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // إنشاء ملف Excel متقدم للورشة المحددة
      const optionsWithWorkshop = { ...exportOptions, workshopName: selectedWorkshop };
      const buffer = await exportToAdvancedExcel(analysisResults, axes, optionsWithWorkshop);
      
      // تحويل إلى Blob وتحميل
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `تحليل_ورشة_${selectedWorkshop}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const responseCount = analysisResults?.by_workshop[selectedWorkshop]?.response_count;
      const responseCountText = (responseCount !== undefined && responseCount !== null) ? responseCount.toString() : 'غير محدد';
      setSuccess(`تم تصدير ملف ورشة "${selectedWorkshop}" بنجاح! يتضمن الملف عدد الاستجابات (${responseCountText}) ورسوماً بيانية ونسب مئوية وتحليلات متقدمة مخصصة للورشة.`);
      
    } catch (error) {
      console.error('Export error:', error);
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء تصدير النتائج');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickExport = async () => {
    const quickOptions: ExportOptions = {
      includeCharts: false,
      includePercentages: true,
      includeDetailedAnalysis: false,
      chartTypes: [],
      colorScheme: 'blue'
    };

    if (!analysisResults) {
      setError('لا توجد نتائج تحليل متاحة للتصدير');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const buffer = await exportToAdvancedExcel(analysisResults, axes, quickOptions);
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ملخص_النتائج_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('تم تصدير الملخص بنجاح!');
      
    } catch (error) {
      console.error('Export error:', error);
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء تصدير النتائج');
    } finally {
      setLoading(false);
    }
  };

  if (!analysisResults) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="text-center py-8">
          <AlertCircle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">لا توجد نتائج للتصدير</h3>
          <p className="mt-1 text-gray-500">يرجى تحليل البيانات أولاً قبل التصدير</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md" dir="rtl">
      <h2 className="text-2xl font-bold mb-6 text-center flex items-center justify-center gap-2">
        <Download className="h-6 w-6" />
        تصدير النتائج المتقدم 📊
      </h2>
      
      {/* اختيار الورشة */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-right flex items-center gap-2">
          🎯 اختيار ورشة العمل للتقييم
        </h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            اختر الورشة المراد تحليلها وتصدير تقريرها:
          </label>
          <select
            value={selectedWorkshop}
            onChange={(e) => setSelectedWorkshop(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
            dir="rtl"
          >
            <option value="">-- اختر ورشة العمل --</option>
            {Object.keys(analysisResults?.by_workshop || {}).map((workshop) => (
              <option key={workshop} value={workshop}>
                {workshop}
              </option>
            ))}
          </select>
        </div>
        
        {selectedWorkshop && (
          <div className="bg-white rounded-lg p-4 border-l-4 border-green-400">
            <h4 className="font-medium text-green-800 mb-2">معاينة الورشة المختارة:</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-gray-600">اسم الورشة:</span>
                <div className="font-semibold text-green-700">{selectedWorkshop}</div>
              </div>
              <div>
                <span className="text-gray-600">عدد الاستجابات:</span>
                <div className="font-semibold text-green-700">
                  {(() => {
                    const responseCount = analysisResults?.by_workshop[selectedWorkshop]?.response_count;
                    return (responseCount !== undefined && responseCount !== null) ? `${responseCount} استجابة` : 'غير محدد';
                  })()}
                </div>
              </div>
              <div>
                <span className="text-gray-600">المتوسط العام:</span>
                <div className="font-semibold text-green-700">
                  {analysisResults?.by_workshop[selectedWorkshop]?.overall_average.toFixed(2)}
                </div>
              </div>
              <div>
                <span className="text-gray-600">مستوى الأداء:</span>
                <div className="font-semibold text-green-700">
                  {analysisResults?.by_workshop[selectedWorkshop]?.performance_level}
                </div>
              </div>
              <div>
                <span className="text-gray-600">عدد المحاور:</span>
                <div className="font-semibold text-green-700">
                  {Object.keys(analysisResults?.by_workshop[selectedWorkshop]?.by_axis || {}).length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* خيارات التصدير المتقدم */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-right flex items-center gap-2">
          <Settings className="h-5 w-5" />
          إعدادات التصدير المتقدم
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* الخيارات الأساسية */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-800">الميزات المضمنة:</h4>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportOptions.includePercentages}
                onChange={(e) => setExportOptions(prev => ({
                  ...prev,
                  includePercentages: e.target.checked
                }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">النسب المئوية للمتوسطات</span>
            </label>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportOptions.includeCharts}
                onChange={(e) => setExportOptions(prev => ({
                  ...prev,
                  includeCharts: e.target.checked
                }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">الرسوم البيانية</span>
            </label>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportOptions.includeDetailedAnalysis}
                onChange={(e) => setExportOptions(prev => ({
                  ...prev,
                  includeDetailedAnalysis: e.target.checked
                }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">التحليل التفصيلي</span>
            </label>
          </div>

          {/* أنواع الرسوم البيانية */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-800">أنواع الرسوم البيانية:</h4>
            
            {['column', 'pie', 'line', 'radar'].map((chartType) => (
              <label key={chartType} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportOptions.chartTypes.includes(chartType as any)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setExportOptions(prev => ({
                        ...prev,
                        chartTypes: [...prev.chartTypes, chartType as any]
                      }));
                    } else {
                      setExportOptions(prev => ({
                        ...prev,
                        chartTypes: prev.chartTypes.filter(ct => ct !== chartType)
                      }));
                    }
                  }}
                  disabled={!exportOptions.includeCharts}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <span className={`text-gray-700 ${!exportOptions.includeCharts ? 'opacity-50' : ''}`}>
                  {chartType === 'column' ? 'أعمدة' : 
                   chartType === 'pie' ? 'دائري' :
                   chartType === 'line' ? 'خطي' : 'رادار'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* نظام الألوان */}
        <div className="mt-6">
          <h4 className="font-medium text-gray-800 mb-3">نظام الألوان:</h4>
          <div className="flex gap-3">
            {(['professional', 'blue', 'green', 'purple'] as const).map((scheme) => (
              <label key={scheme} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="colorScheme"
                  value={scheme}
                  checked={exportOptions.colorScheme === scheme}
                  onChange={(e) => setExportOptions(prev => ({
                    ...prev,
                    colorScheme: e.target.value as any
                  }))}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">
                  {scheme === 'professional' ? 'مهني' :
                   scheme === 'blue' ? 'أزرق' :
                   scheme === 'green' ? 'أخضر' : 'بنفسجي'}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* أزرار التصدير */}
      <div className="space-y-4">
        {/* التصدير المتقدم */}
        <div className="bg-white border-2 border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-right">
              <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                تصدير متقدم - ورشة واحدة
              </h3>
              <p className="text-green-600 text-sm mt-1">
                تقرير شامل ومخصص للورشة المختارة مع رسوم بيانية حقيقية
              </p>
            </div>
            <button
              onClick={handleAdvancedExport}
              disabled={loading || !selectedWorkshop}
              className="flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                <Download className="h-5 w-5" />
              )}
              <span>تصدير ورشة {selectedWorkshop ? `"${selectedWorkshop}"` : ''}</span>
            </button>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">محتويات تقرير الورشة المتقدم:</h4>
            <ul className="text-green-700 text-sm space-y-1 list-disc list-inside">
              <li>📋 ملخص تنفيذي خاص بالورشة المختارة مع عدد الاستجابات</li>
              <li>📊 تحليل مفصل لكل محور مع النسب المئوية</li>
              <li>📈 رسوم بيانية حقيقية داخل Excel (أعمدة ودائرية)</li>
              <li>🎯 توصيات مخصصة لهذه الورشة تحديداً</li>
              <li>📋 خطة تحسين مفصلة للمحاضر والإدارة</li>
              <li>🔢 إحصائيات متقدمة مع تفاصيل الاستجابات</li>
              <li>⭐ تحليل نقاط القوة والضعف للورشة</li>
              <li>📝 توصيات محددة لكل سؤال في كل محور</li>
            </ul>
            
            {selectedWorkshop && (
              <div className="mt-3 p-3 bg-white rounded border border-green-200">
                <p className="text-green-800 text-xs">
                  🎉 سيتم إنشاء تقرير شامل ومخصص بالكامل لورشة <strong>"{selectedWorkshop}"</strong> 
                  مع تحليلات وتوصيات مفصلة خاصة بهذه الورشة وحدها.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* التصدير السريع */}
        <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-right">
              <h3 className="text-lg font-semibold text-blue-800 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                تصدير سريع - ملخص
              </h3>
              <p className="text-blue-600 text-sm mt-1">
                ملخص سريع مع النسب المئوية بدون رسوم بيانية
              </p>
            </div>
            <button
              onClick={handleQuickExport}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                <Download className="h-5 w-5" />
              )}
              <span>تصدير سريع</span>
            </button>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">محتويات الملخص السريع:</h4>
            <ul className="text-blue-700 text-sm space-y-1 list-disc list-inside">
              <li>الملخص العام للنتائج</li>
              <li>النسب المئوية للمتوسطات</li>
              <li>مقارنة سريعة بين ورش العمل</li>
              <li>ترتيب المحاور حسب الأداء</li>
            </ul>
          </div>
        </div>
      </div>

      {/* رسائل الحالة */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mt-4 p-4 bg-green-50 text-green-600 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      {/* معلومات إضافية */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-800 mb-2">💡 اقتراحات خبراء التدريب والجودة:</h4>
        <ul className="text-gray-600 text-sm space-y-1 list-disc list-inside">
          <li><strong>للمحاضرين:</strong> كل ورشة لها خصائصها المستقلة - راجع التقرير المخصص لورشتك لتحديد نقاط التطوير</li>
          <li><strong>للإدارة التدريبية:</strong> قارن تقارير الورش المختلفة لتحديد أفضل الممارسات والمحاضرين المتميزين</li>
          <li><strong>لمنسقي الجودة:</strong> استخدم الإحصائيات المتقدمة لكل ورشة لضمان المعايير المطلوبة</li>
          <li><strong>للمتابعة الدورية:</strong> صدّر تقرير الورشة بعد كل دورة تدريبية لبناء قاعدة بيانات تطوير مستمر</li>
          <li><strong>للتطوير المهني:</strong> احتفظ بتقارير ورشك الشخصية لتتبع تطورك كمحاضر عبر الزمن</li>
        </ul>
        
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
          <p className="text-blue-800 text-sm">
            <strong>🎯 فلسفة التقييم المنفصل:</strong> كل ورشة تدريبية لها محاضر مختلف، ومكان مختلف، وجمهور مختلف، 
            وطريقة تنظيم مختلفة. لذلك يجب تقييم كل ورشة بشكل مستقل للحصول على تحليل دقيق وتوصيات مفيدة.
          </p>
        </div>
      </div>
    </div>
  );
}